/**
 * Material Transfer Note (Internal-Arrival), Processing → Packing (`TMN/…`, design
 * docs/designs/mtn-seed-indent.md, user 2026-11-04). Today a handwritten note: Processing lists the
 * Condition lots it hands to Packing (crop, variety, lot, M %, G %, DOT, bags, kg), Packing receives
 * them into its bins and the supervisor approves. Several crops and varieties may ride one note.
 *
 * Its own module (`/mtn`), not a Processing-engine form: Issue moves each pick out of its CSW bin and
 * holds it against the note (ledger `activity`, location = the note), the rest of a Partial lot stays
 * in its bin under the same number; Receive puts what arrived into the Packing bins.
 */
import type { DocStatus } from "../status";
import { slocUpdation } from "./processing";
import type { FormConfig } from "./types";

export const MTN_FORM = "processing_mtn";

export const mtnForm: FormConfig = {
  ...slocUpdation,
  code: MTN_FORM,
  docType: "packaging",
  title: "Material Transfer Note",
  numberPrefix: "TMN",
  raisedOnHandheld: false,
  lineColumns: ["lot_number", "crop", "variety", "stage", "actions"],
};

/** Where a note is: the engine's status, then the step it reached after Issue (`extra.mtnStage`). */
export const MTN_STAGES = [
  "draft",
  "previewed",
  "issued",
  "received",
  "approved",
  /** Approved, and every kg received is on WB Packing Slips that are packed: read, never stored. */
  "closed",
  "cancelled",
] as const;
export type MtnStage = (typeof MTN_STAGES)[number];

export const MTN_STAGE_LABELS: Readonly<Record<MtnStage, string>> = {
  draft: "Draft",
  previewed: "Previewed",
  issued: "Issued – with Packing",
  received: "Received",
  approved: "Approved",
  closed: "Closed – all packed",
  cancelled: "Cancelled",
};

export function mtnStageOf(
  status: DocStatus,
  extra: Record<string, unknown> | null | undefined
): MtnStage {
  if (status === "draft" || status === "previewed" || status === "cancelled")
    return status;
  const st = extra?.mtnStage;
  return st === "received" || st === "approved" ? st : "issued";
}

/** One code per step (Roles & Permissions ticks): Processing issues, Packing receives and approves. */
export const MTN_CODES = {
  issue: "processing.mtn.issue",
  receive: "packaging.mtn.receive",
  approve: "packaging.mtn.approve",
} as const;

/** What happens to seed issued but not received: back to the bins it came from, or a transit loss. */
export const MTN_SHORTAGE = ["return", "loss"] as const;
export type MtnShortage = (typeof MTN_SHORTAGE)[number];

/** The loss type a transit shortage is booked under (migration 0200). */
export const MTN_TRANSIT_LOSS = "TRL";
